let isolatedBuyButtonsController = null;
let isolatedProductCardsController = null;
let isolatedCartSyncId = 0;

const isolatedSkus = new Set([
  '100-00002',
  '100-00003'
]);

let isolatedCartState = null;


function getIsolatedCartState(cart) {
  const state = {
    hasIsolatedProducts: false,
    hasOtherProducts: false
  };

  if (!cart?.items) return state;

  cart.items.forEach((item) => {
    if (isolatedSkus.has(item.sku)) {
      state.hasIsolatedProducts = true;
    } else {
      state.hasOtherProducts = true;
    }
  });

  return state;
}


function isolatedCartStateChanged(previousState, nextState) {
  if (!previousState) return true;

  return (
    previousState.hasIsolatedProducts !== nextState.hasIsolatedProducts ||
    previousState.hasOtherProducts !== nextState.hasOtherProducts
  );
}


async function fetchCurrentCart() {
  const response = await fetch('/cart.js', {
    cache: 'no-store',
    headers: {
      Accept: 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Unable to fetch cart: ${response.status}`);
  }

  return response.json();
}


async function initialiseIsolatedCartState() {
  const syncId = ++isolatedCartSyncId;

  try {
    const cart = await fetchCurrentCart();

    if (syncId !== isolatedCartSyncId) return;

    isolatedCartState = getIsolatedCartState(cart);
  } catch (error) {
    console.error(
      'Unable to initialise isolated cart state:',
      error
    );
  }
}


async function syncIsolatedCartState({
  forceRefresh = false
} = {}) {
  const syncId = ++isolatedCartSyncId;

  try {
    const cart = await fetchCurrentCart();

    if (syncId !== isolatedCartSyncId) return;

    const previousState = isolatedCartState;
    const nextState = getIsolatedCartState(cart);

    const stateChanged = isolatedCartStateChanged(
      previousState,
      nextState
    );

    isolatedCartState = nextState;

    const isolatedStateRelevant =
      previousState?.hasIsolatedProducts === true ||
      nextState.hasIsolatedProducts === true;

    if (
      !forceRefresh &&
      (!stateChanged || !isolatedStateRelevant)
    ) {
      return;
    }

    await Promise.all([
      refreshIsolatedBuyButtons(),
      refreshIsolatedProductCardSections()
    ]);
  } catch (error) {
    console.error(
      'Unable to sync isolated cart state:',
      error
    );
  }
}


async function refreshIsolatedBuyButtons(
  variantId = null,
  sourceElement = null
) {
  let wrappers = [];

  if (sourceElement) {
    const wrapper = sourceElement.closest(
      '[data-isolated-buy-buttons]'
    );

    if (wrapper) {
      wrappers = [wrapper];
    }
  }

  if (!wrappers.length) {
    wrappers = [
      ...document.querySelectorAll(
        '[data-isolated-buy-buttons]'
      )
    ];
  }

  if (!wrappers.length) return;

  if (isolatedBuyButtonsController) {
    isolatedBuyButtonsController.abort();
  }

  isolatedBuyButtonsController = new AbortController();

  const { signal } = isolatedBuyButtonsController;

  try {
    await Promise.all(
      wrappers.map(async (wrapper) => {
        const sectionId = wrapper.dataset.sectionId;

        if (!sectionId) return;

        const currentVariantId =
          variantId ||
          wrapper.dataset.variantId ||
          new URLSearchParams(
            window.location.search
          ).get('variant');

        const url = new URL(window.location.href);

        url.searchParams.set('sections', sectionId);

        if (currentVariantId) {
          url.searchParams.set(
            'variant',
            currentVariantId
          );
        }

        const response = await fetch(
          url.toString(),
          {
            signal,
            cache: 'no-store',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) return;

        const sections = await response.json();
        const html = sections[sectionId];

        if (!html) return;

        const parsed = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const selector =
          `[data-isolated-buy-buttons]` +
          `[data-section-id="${CSS.escape(sectionId)}"]`;

        const newWrappers = [
          ...parsed.querySelectorAll(selector)
        ];

        const currentWrappers = [
          ...document.querySelectorAll(selector)
        ];

        const index = currentWrappers.indexOf(wrapper);

        const newWrapper =
          newWrappers[index] ||
          newWrappers[0];

        if (
          !newWrapper ||
          !wrapper.isConnected
        ) {
          return;
        }

        wrapper.replaceWith(newWrapper);
      })
    );
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error(
        'Unable to refresh isolated buy buttons:',
        error
      );
    }
  }
}


async function refreshIsolatedProductCardSections() {
  const wrappers = [
    ...document.querySelectorAll(
      '[data-isolated-product-card-section]'
    )
  ];

  if (!wrappers.length) return;

  const sectionIds = [
    ...new Set(
      wrappers
        .map((wrapper) => wrapper.dataset.sectionId)
        .filter(Boolean)
    )
  ];

  if (!sectionIds.length) return;

  if (isolatedProductCardsController) {
    isolatedProductCardsController.abort();
  }

  isolatedProductCardsController =
    new AbortController();

  const { signal } =
    isolatedProductCardsController;

  try {
    await Promise.all(
      sectionIds.map(async (sectionId) => {
        const url = new URL(
          window.location.href
        );

        url.searchParams.set(
          'sections',
          sectionId
        );

        const response = await fetch(
          url.toString(),
          {
            signal,
            cache: 'no-store',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest'
            }
          }
        );

        if (!response.ok) return;

        const renderedSections =
          await response.json();

        const html =
          renderedSections[sectionId];

        if (!html) return;

        const parsed =
          new DOMParser().parseFromString(
            html,
            'text/html'
          );

        const selector =
          `[data-isolated-product-card-section]` +
          `[data-section-id="${CSS.escape(sectionId)}"]`;

        const currentWrappers = [
          ...document.querySelectorAll(selector)
        ];

        const newWrappers = [
          ...parsed.querySelectorAll(selector)
        ];

        currentWrappers.forEach(
          (wrapper, index) => {
            const newWrapper =
              newWrappers[index];

            if (
              !newWrapper ||
              !wrapper.isConnected
            ) {
              return;
            }

            wrapper.replaceWith(newWrapper);
          }
        );
      })
    );
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error(
        'Unable to refresh isolated product card sections:',
        error
      );
    }
  }
}
let isolatedCartSyncTimeout = null;
let isolatedCartVerifyTimeout = null;

function scheduleIsolatedCartSync() {
  clearTimeout(isolatedCartSyncTimeout);
  clearTimeout(isolatedCartVerifyTimeout);

  isolatedCartSyncTimeout = window.setTimeout(() => {
    syncIsolatedCartState({
      forceRefresh: true
    });
  }, 150);

  isolatedCartVerifyTimeout = window.setTimeout(() => {
    syncIsolatedCartState({
      forceRefresh: true
    });
  }, 700);
}

document.addEventListener(
  'on:variant:change',
  (event) => {
    const variantId =
      event.detail?.variant?.id;

    const form = event.detail?.form;

    if (!variantId) return;

    refreshIsolatedBuyButtons(
      variantId,
      form instanceof Element
        ? form
        : null
    );
  }
);

document.addEventListener(
  'on:cart:add',
  () => {
    scheduleIsolatedCartSync();
  }
);
document.addEventListener(
  'on:line-item:change',
  () => {
    scheduleIsolatedCartSync();
  }
);


document.addEventListener(
  'on:quickbuy:after-open',
  () => {
    syncIsolatedCartState({
      forceRefresh: true
    });
  }
);


initialiseIsolatedCartState();